// apps/roleforge-app/src/lib/use-operating-model-state.ts
//
// SSE-driven operating-model state. Lifted out of OperatingModelPanel so the
// new layout (AssetsColumn + AssetEditorPane) can read the same state from
// either the right column (cards) or the middle column (full editor) without
// duplicate subscriptions. The router still owns event delivery.

import { useEffect, useRef, useState } from 'react';
import type { SseEventRouter } from './sse-event-router';
import type { AuditRow, Opportunity, OpsCanvasEngineRow } from './api-client';
import { gapAutoPromotionQuadrant, type CandidateClass } from '@vibecontrol/shared/roleforge';

export interface OperatingModelEngine {
  engine_id: string;
  name: string;
  vertical_template: string;
  steps: Array<{ step_id: string; name: string; time_sink: boolean; quality_risk: boolean }>;
}

export interface OperatingModelOpportunity {
  opportunity_id: string;
  title: string;
  engine_id: string | null;
  candidate_class: 'standard' | 'foundation' | 'gap';
  source_message_id: string | null;
  roi?: { annual_savings: number };
}

export interface OperatingModelPainPoint {
  id: string;
  summary: string;
  source_message_id: string | null;
  recorded_at: string | null;
}

export type OperatingModelQuadrant =
  | 'quick_wins'
  | 'big_swings'
  | 'nice_to_haves'
  | 'deprioritize';

export interface OperatingModelState {
  engines: OperatingModelEngine[];
  painPoints: OperatingModelPainPoint[];
  opportunities: OperatingModelOpportunity[];
  matrixAssembled: boolean;
  assignments: Map<string, OperatingModelQuadrant>;
  totalSavings: number;
}

export interface PersistedOperatingModelInput {
  audit?: AuditRow | null;
  opsCanvas?: OpsCanvasEngineRow[];
  opportunities?: Opportunity[];
}

function normalizeCandidateClass(value: unknown): CandidateClass {
  return value === 'foundation' || value === 'gap' ? value : 'standard';
}

/**
 * Review fix (#1329): the ONE place a pain-point id is minted.
 *
 * There were two — the persisted mapper produced `pain-${sourceMessageId}-${index}`
 * and the SSE handler produced `pain-${source_message_id}` — so a persisted row
 * and its live twin never matched, and merging rendered two cards for one pain
 * point at every audit-status transition. Duplicating the format was the actual
 * defect, so it is now impossible to duplicate: both callers go through here.
 *
 * `source_message_id` is the only key the two sides share (the SSE payload
 * carries no row id). Without one, fall back to the summary, which is what the
 * SSE handler already deduped on.
 */
export function painPointId(sourceMessageId: string | null | undefined, summary: string): string {
  return sourceMessageId ? `pain-${sourceMessageId}` : `pain-${summary}`;
}

/**
 * #1329: a STABLE identity for a set of persisted rows.
 *
 * The reset effect below used to depend on
 * `[persisted.audit, persisted.opsCanvas, persisted.opportunities]` with no
 * equality guard. Those arrive as a fresh object/array literal from
 * `AuditPersistentShell` and are re-parsed after every send, so their identity
 * changed on every turn even when nothing had actually changed — and the effect
 * then fired five `setState` calls that DISCARDED live SSE state. Cards that had
 * just animated in blinked out; ROI values that had arrived by event but not yet
 * by row were dropped.
 *
 * The revision is `audit.id` plus an `updated_at` high-water mark and row count
 * per collection. Two loads of unchanged data produce the same string, so the
 * effect does not fire. A real change (a new row, an edited row, a different
 * audit) moves the mark and it does.
 */
export function persistedRevision(persisted: PersistedOperatingModelInput): string {
  const mark = (rows: Array<{ id: string; updated_at?: string }> | undefined): string => {
    if (!rows || rows.length === 0) return '0:';
    let high = '';
    for (const row of rows) {
      const stamp = row.updated_at ?? '';
      if (stamp > high) high = stamp;
    }
    // Count is part of the mark so a DELETION (which lowers no high-water mark)
    // is still a revision change.
    return `${rows.length}:${high}`;
  };
  return [
    persisted.audit?.id ?? '-',
    persisted.audit?.status ?? '-',
    // Review fix (#1329): pain points need a CONTENT mark, not a timestamp mark.
    //
    // They live in `audit.metadata.pain_points`, and `AuditRow` has no
    // `updated_at` column at all — so the high-water mark this function was
    // written around never existed for the audit. Keying on `audit.id` + status
    // alone meant a newly persisted pain point produced an IDENTICAL revision,
    // the effect never re-ran, and the pain point stayed invisible until a status
    // change or a reload. On `main` the identity churn happened to re-parse and
    // surface it, so this was a regression for any pain point whose SSE event was
    // missed (reconnect, dropped event, late subscription).
    painPointMark(persisted.audit),
    mark(persisted.opsCanvas),
    mark(persisted.opportunities),
  ].join('|');
}

/**
 * Content mark for `audit.metadata.pain_points`: count plus a cheap order-
 * independent digest of the summaries. Order-independent so a server-side
 * reordering of an unchanged set does not churn the effect; content-sensitive so
 * an edited summary does.
 */
function painPointMark(audit?: AuditRow | null): string {
  const rows = mapPersistedPainPoints(audit);
  if (rows.length === 0) return 'p0';
  let digest = 0;
  for (const row of rows) {
    // Sum per-row hashes so the result does not depend on iteration order.
    let rowHash = 0;
    const key = `${row.id}\u0000${row.summary}`;
    for (let i = 0; i < key.length; i += 1) {
      rowHash = (rowHash * 31 + key.charCodeAt(i)) | 0;
    }
    digest = (digest + rowHash) | 0;
  }
  return `p${rows.length}:${digest}`;
}

/**
 * #1329: merge persisted rows over live SSE rows, keyed by id.
 *
 * A straight replace is what dropped live state. Persisted rows WIN for any id
 * present in both (the database is the durable truth), and live-only rows are
 * KEPT — those are the cards that arrived by event and have not been written
 * yet. Live-only rows are appended in their existing order, after the persisted
 * ones, so the persisted ordering is preserved.
 */
export function mergeById<T>(persistedRows: T[], liveRows: T[], idOf: (row: T) => string): T[] {
  const persistedIds = new Set(persistedRows.map(idOf));
  const liveOnly = liveRows.filter((row) => !persistedIds.has(idOf(row)));
  return [...persistedRows, ...liveOnly];
}

/**
 * Review fix (#1329): pain points merge on id OR summary.
 *
 * The SSE handler already dedupes on `existing.id === id || existing.summary ===
 * p.summary`, because the payload carries no row id and the same pain point can
 * legitimately arrive under a different message id. `mergeById` matched on id
 * only, so a persisted row and its live twin both survived — two cards for one
 * pain point at every audit-status transition, which is the normal progression.
 *
 * Aligning the merge with the handler's own dedupe rule is the fix; matching ids
 * (above) is what makes the common case cheap.
 */
export function mergePainPoints(
  persistedRows: OperatingModelPainPoint[],
  liveRows: OperatingModelPainPoint[],
): OperatingModelPainPoint[] {
  const ids = new Set(persistedRows.map((row) => row.id));
  const summaries = new Set(persistedRows.map((row) => row.summary));
  const liveOnly = liveRows.filter((row) => !ids.has(row.id) && !summaries.has(row.summary));
  return [...persistedRows, ...liveOnly];
}

function mapPersistedEngines(rows: OpsCanvasEngineRow[] = []): OperatingModelEngine[] {
  return rows
    .slice()
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((row) => ({
      engine_id: row.engine,
      name: row.name,
      vertical_template: row.engine,
      steps: (row.steps ?? []).map((step, index) => {
        const s = step as {
          id?: string;
          step_id?: string;
          name?: string;
          time_sink?: boolean;
          quality_risk?: boolean;
        };
        return {
          step_id: s.id ?? s.step_id ?? `${row.engine}-step-${index}`,
          name: s.name ?? `Step ${index + 1}`,
          time_sink: Boolean(s.time_sink),
          quality_risk: Boolean(s.quality_risk),
        };
      }),
    }));
}

function mapPersistedOpportunities(rows: Opportunity[] = []): OperatingModelOpportunity[] {
  return rows
    .filter((row) => row.applies !== false)
    .map((row) => {
      const metadata = row.metadata ?? {};
      const roi = metadata.roi as { annual_savings?: unknown } | undefined;
      const annualSavings = Number(roi?.annual_savings ?? 0);
      return {
        opportunity_id: row.id,
        title: row.name,
        engine_id: row.engine ?? null,
        candidate_class: normalizeCandidateClass(row.candidate_class ?? metadata.candidate_class),
        source_message_id:
          typeof metadata.source_message_id === 'string' ? metadata.source_message_id : null,
        ...(annualSavings > 0 ? { roi: { annual_savings: annualSavings } } : {}),
      };
    });
}

function mapPersistedPainPoints(audit?: AuditRow | null): OperatingModelPainPoint[] {
  const raw = audit?.metadata?.pain_points;
  if (!Array.isArray(raw)) return [];

  return raw.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const row = item as {
      summary?: unknown;
      description?: unknown;
      source_message_id?: unknown;
      recorded_at?: unknown;
    };
    const summary = typeof row.summary === 'string'
      ? row.summary
      : typeof row.description === 'string'
        ? row.description
        : '';
    if (!summary.trim()) return [];
    const sourceMessageId = typeof row.source_message_id === 'string' ? row.source_message_id : null;
    const recordedAt = typeof row.recorded_at === 'string' ? row.recorded_at : null;
    return [{
      // Review fix (#1329): minted by the SHARED `painPointId`, so this can no
      // longer drift from what the `pain_point.recorded` handler produces. The
      // old `-${index}` suffix made a mismatch guaranteed, so merging rendered
      // two cards for one pain point and `AssetsColumn` reported "2 pain points
      // recorded".
      id: painPointId(sourceMessageId, summary),
      summary,
      source_message_id: sourceMessageId,
      recorded_at: recordedAt,
    }];
  });
}

function mapPersistedAssignments(rows: Opportunity[] = []): Map<string, OperatingModelQuadrant> {
  const next = new Map<string, OperatingModelQuadrant>();
  for (const row of rows) {
    if (row.applies === false) continue;
    const candidateClass = normalizeCandidateClass(row.candidate_class ?? row.metadata?.candidate_class);
    next.set(
      row.id,
      candidateClass === 'gap'
        ? gapAutoPromotionQuadrant(0, 0)
        : row.quadrant,
    );
  }
  return next;
}

export function useOperatingModelState(
  router: SseEventRouter,
  persisted: PersistedOperatingModelInput = {},
): OperatingModelState {
  const [engines, setEngines] = useState<OperatingModelEngine[]>(() =>
    mapPersistedEngines(persisted.opsCanvas),
  );
  const [painPoints, setPainPoints] = useState<OperatingModelPainPoint[]>(() =>
    mapPersistedPainPoints(persisted.audit),
  );
  const [opportunities, setOpportunities] = useState<OperatingModelOpportunity[]>(() =>
    mapPersistedOpportunities(persisted.opportunities),
  );
  const [matrixAssembled, setMatrixAssembled] = useState(
    () => (persisted.opportunities?.filter((row) => row.applies !== false).length ?? 0) > 0,
  );
  const [assignments, setAssignments] = useState<Map<string, OperatingModelQuadrant>>(() =>
    mapPersistedAssignments(persisted.opportunities),
  );
  const candidateClassByOppRef = useRef<Map<string, CandidateClass>>(new Map());

  // #1329: keyed on a stable revision string, not on object identity, and
  // merged by id rather than replaced. `persistedRef` lets the effect read the
  // latest props without taking them as dependencies — the revision is the only
  // thing allowed to trigger a reset.
  const persistedRef = useRef(persisted);
  persistedRef.current = persisted;
  const revision = persistedRevision(persisted);
  // Review fix (#1329): which audit the current live state belongs to.
  //
  // Merging is only ever correct WITHIN one audit. `AuditExperienceShell` does
  // not unmount on a param change, so navigating `/audits/A → /audits/B`
  // re-renders this hook with B's props while A's rows are still in state — and a
  // merge would keep A's rows visible under B. Before this PR the unconditional
  // replace made that safe by accident. A different audit id now REPLACES rather
  // than merges, which restores that property deliberately instead of relying on
  // the navigation being unreachable.
  const liveAuditIdRef = useRef<string | null>(persisted.audit?.id ?? null);

  useEffect(() => {
    const current = persistedRef.current;
    const auditId = current.audit?.id ?? null;
    // A different audit means the live state belongs to something else.
    const sameAudit = liveAuditIdRef.current === auditId;
    liveAuditIdRef.current = auditId;
    const keep = <T,>(live: T[]): T[] => (sameAudit ? live : []);

    setEngines((live) =>
      mergeById(mapPersistedEngines(current.opsCanvas), keep(live), (e) => e.engine_id),
    );
    setPainPoints((live) => mergePainPoints(mapPersistedPainPoints(current.audit), keep(live)));
    setOpportunities((live) =>
      mergeById(mapPersistedOpportunities(current.opportunities), keep(live), (o) => o.opportunity_id),
    );
    // `matrixAssembled` is STICKY within an audit: an event can flip it true
    // before the rows land, and a persisted read that has not caught up must not
    // flip it back and un-render the matrix. Crossing to a different audit resets
    // it, so the stickiness cannot leak between audits.
    setMatrixAssembled((prev) => {
      const fromPersisted =
        (current.opportunities?.filter((row) => row.applies !== false).length ?? 0) > 0;
      return sameAudit ? prev || fromPersisted : fromPersisted;
    });
    setAssignments((live) => {
      // Persisted assignments win; a live-only assignment (assigned by event,
      // not yet a row) is kept.
      const merged = new Map(sameAudit ? live : []);
      for (const [id, quadrant] of mapPersistedAssignments(current.opportunities)) {
        merged.set(id, quadrant);
      }
      return merged;
    });
    if (!sameAudit) candidateClassByOppRef.current.clear();
    for (const row of current.opportunities ?? []) {
      if (row.applies === false) continue;
      candidateClassByOppRef.current.set(
        row.id,
        normalizeCandidateClass(row.candidate_class ?? row.metadata?.candidate_class),
      );
    }
  }, [revision]);

  useEffect(() => {
    const u1 = router.subscribe('operating_model', 'engine.created', (ev) => {
      if (ev.payload.kind !== 'engine.created') return;
      const p = ev.payload;
      setEngines((prev) =>
        prev.some((e) => e.engine_id === p.engine_id)
          ? prev
          : [
              ...prev,
              {
                engine_id: p.engine_id,
                name: p.name,
                vertical_template: p.vertical_template,
                steps: [],
              },
            ],
      );
    });
    const u2 = router.subscribe('operating_model', 'engine.step.added', (ev) => {
      if (ev.payload.kind !== 'engine.step.added') return;
      const p = ev.payload;
      setEngines((prev) =>
        prev.map((e) =>
          e.engine_id === p.engine_id
            ? {
                ...e,
                steps: [
                  ...e.steps,
                  { step_id: p.step_id, name: p.name, time_sink: false, quality_risk: false },
                ],
              }
            : e,
        ),
      );
    });
    const u3 = router.subscribe('operating_model', 'engine.step.tagged', (ev) => {
      if (ev.payload.kind !== 'engine.step.tagged') return;
      const p = ev.payload;
      setEngines((prev) =>
        prev.map((e) => ({
          ...e,
          steps: e.steps.map((s) =>
            s.step_id === p.step_id ? { ...s, [p.tag]: true } : s,
          ),
        })),
      );
    });
    const u4 = router.subscribe('operating_model', 'opportunity.surfaced', (ev) => {
      if (ev.payload.kind !== 'opportunity.surfaced') return;
      const p = ev.payload;
      candidateClassByOppRef.current.set(p.opportunity_id, p.candidate_class);
      setOpportunities((prev) =>
        prev.some((o) => o.opportunity_id === p.opportunity_id)
          ? prev
          : [
              ...prev,
              {
                opportunity_id: p.opportunity_id,
                title: p.title,
                engine_id: p.engine_id,
                source_message_id: p.source_message_id,
                candidate_class: p.candidate_class,
              },
            ],
      );
    });
    const u7 = router.subscribe('operating_model', 'pain_point.recorded', (ev) => {
      if (ev.payload.kind !== 'pain_point.recorded') return;
      const p = ev.payload;
      setPainPoints((prev) => {
        // Same minting function as the persisted mapper — see `painPointId`.
        const id = painPointId(p.source_message_id, p.summary);
        if (prev.some((existing) => existing.id === id || existing.summary === p.summary)) {
          return prev;
        }
        return [
          ...prev,
          {
            id,
            summary: p.summary,
            source_message_id: p.source_message_id,
            recorded_at: new Date().toISOString(),
          },
        ];
      });
    });
    const u5 = router.subscribe('operating_model', 'opportunity.scored', (ev) => {
      if (ev.payload.kind !== 'opportunity.scored') return;
      const p = ev.payload;
      setOpportunities((prev) =>
        prev.map((o) =>
          o.opportunity_id === p.opportunity_id
            ? { ...o, roi: { annual_savings: p.annual_savings } }
            : o,
        ),
      );
    });
    const u6 = router.subscribe('operating_model', 'synthesis.matrix.assembled', (ev) => {
      if (ev.payload.kind !== 'synthesis.matrix.assembled') return;
      const p = ev.payload;
      setMatrixAssembled(true);
      const next = new Map<string, OperatingModelQuadrant>();
      for (const a of p.quadrant_assignments) {
        const cc = candidateClassByOppRef.current.get(a.opportunity_id);
        if (cc === 'gap') {
          next.set(a.opportunity_id, gapAutoPromotionQuadrant(0, 0));
        } else {
          next.set(a.opportunity_id, a.quadrant);
        }
      }
      setAssignments(next);
    });
    return () => {
      u1();
      u2();
      u3();
      u4();
      u5();
      u6();
      u7();
    };
  }, [router]);

  const totalSavings = opportunities.reduce((acc, o) => acc + (o.roi?.annual_savings ?? 0), 0);

  return { engines, painPoints, opportunities, matrixAssembled, assignments, totalSavings };
}
